import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { analytics } from '../hooks/useAnalytics';
import { useTranslation } from '../i18n';
import { DirectionalIcon } from '../components/ui/DirectionalIcon';
import { STALE_TIMES } from '../constants/staleTimes';
import { useAuthStore } from '../store/authStore';

// ─── types ────────────────────────────────────────────────────────────────────

interface Schedule {
  id: string;
  days_of_week: number[];
  start_time: string;
  end_time: string;
  is_active: boolean;
}

interface SpotSummary {
  spot_number: string;
  level_code: string;
  level_label: string;
  status: 'free' | 'occupied' | 'unavailable';
  active_session: { occupant: { apartment: string } } | null;
}

interface DaySchedule {
  dayNum: number;
  startTime: string;
  endTime: string;
}

// ─── constants ────────────────────────────────────────────────────────────────

const SCHEDULE_MESSAGES = [
  'My spot will no longer be available on a scheduled basis, I will need my spot soon',
  'My spot will no longer be available on a scheduled basis, you can stay until the end today',
] as const;

function getDays(t: (key: string) => string, language?: string) {
  const days = [
    { label: 'M', full: t('day_mon'), dayNum: 1 },
    { label: 'T', full: t('day_tue'), dayNum: 2 },
    { label: 'W', full: t('day_wed'), dayNum: 3 },
    { label: 'T', full: t('day_thu'), dayNum: 4 },
    { label: 'F', full: t('day_fri'), dayNum: 5 },
    { label: 'S', full: t('day_sat'), dayNum: 6 },
    { label: 'S', full: t('day_sun'), dayNum: 7 },
  ];

  // In Hebrew/Israel, show Saturday as the last day of the week
  if (language === 'he') {
    return [
      { label: 'א', full: t('day_sun'), dayNum: 7 },
      { label: 'ב', full: t('day_mon'), dayNum: 1 },
      { label: 'ג', full: t('day_tue'), dayNum: 2 },
      { label: 'ד', full: t('day_wed'), dayNum: 3 },
      { label: 'ה', full: t('day_thu'), dayNum: 4 },
      { label: 'ו', full: t('day_fri'), dayNum: 5 },
      { label: 'ש', full: t('day_sat'), dayNum: 6 },
    ];
  }

  return days;
}

function formatDays(days: number[], t: (key: string) => string, language?: string): string {
  if (!days.length) return '';
  const DAYS = getDays(t, language);
  const dayMap = new Map(DAYS.map((d) => [d.dayNum, d.full]));
  const sorted = [...days].sort((a, b) => a - b);
  const ranges: string[] = [];
  let start = sorted[0], end = sorted[0];
  for (let i = 1; i <= sorted.length; i++) {
    if (i < sorted.length && sorted[i] === end + 1) {
      end = sorted[i];
    } else {
      ranges.push(start === end ? dayMap.get(start)! : `${dayMap.get(start)}–${dayMap.get(end)}`);
      if (i < sorted.length) { start = sorted[i]; end = sorted[i]; }
    }
  }
  return ranges.join(', ');
}

// ─── SchedulePage ─────────────────────────────────────────────────────────────

export default function SchedulePage() {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc       = useQueryClient();
  const { t, language } = useTranslation();
  // Building's workweek as ISO weekday integers (1=Mon..7=Sun). Drives the
  // "Weekdays" preset in the copy-hours panel so users on Mon-Fri buildings
  // don't get tricked by an Israeli Sun-Thu shortcut. Falls back to the
  // Israeli default when /users/me hasn't populated workweekDays yet
  // (older API or pre-auth-refresh cached payload).
  const workweekDays: number[] =
    (useAuthStore.getState().user as any)?.building?.workweekDays ?? [1, 2, 3, 4, 7];

  // ── Core scheduling state ──
  const [days, setDays] = useState<DaySchedule[]>([
    { dayNum: 1, startTime: '08:00', endTime: '18:00' },
    { dayNum: 2, startTime: '08:00', endTime: '18:00' },
    { dayNum: 3, startTime: '08:00', endTime: '18:00' },
    { dayNum: 4, startTime: '08:00', endTime: '18:00' },
    { dayNum: 5, startTime: '08:00', endTime: '18:00' },
    { dayNum: 6, startTime: '08:00', endTime: '18:00' },
    { dayNum: 7, startTime: '08:00', endTime: '18:00' },
  ]);
  const [selectedDays, setSelectedDays] = useState<number[]>([]);

  // ── UI state ──
  const [isActive, setIsActive] = useState(true);
  const [existingId, setExistingId] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [sendingMessage, setSendingMessage]       = useState(false);
  // Which day's "copy hours to…" panel is currently open. Only one row's
  // panel can be open at a time. `targetSelection` holds the days the user
  // has chosen to receive the source row's start/end times.
  const [copyMenuFor, setCopyMenuFor] = useState<number | null>(null);
  const [copyTargets, setCopyTargets] = useState<number[]>([]);

  const { data: spotData } = useQuery({
    queryKey: ['spot', id],
    queryFn:  () => apiClient.get<{ data: SpotSummary }>(`/spots/${id}`).then((r) => r.data.data),
    enabled:  !!id,
  });

  const { data: schedules } = useQuery({
    queryKey: ['schedules', id],
    queryFn:  () => apiClient.get<{ data: Schedule[] }>(`/spots/${id}/schedules`).then((r) => r.data.data),
    enabled:  !!id,
    refetchOnMount: true,
    staleTime: STALE_TIMES.SLOW,
  });

  // ── Load existing schedules into days and selectedDays ──
  useEffect(() => {
    if (!schedules || schedules.length === 0) return;

    setExistingId(schedules[0].id);
    setIsActive(schedules[0].is_active);

    // Populate days with their times from loaded schedules
    const newDays = days.map((day) => {
      const schedule = schedules.find((s) => s.days_of_week.includes(day.dayNum));
      if (!schedule) return day;

      return {
        dayNum: day.dayNum,
        startTime: schedule.start_time.slice(0, 5),
        endTime: schedule.end_time.slice(0, 5),
      };
    });
    setDays(newDays);

    // Populate selectedDays from all schedules
    const allSelectedDays = schedules.flatMap((s) => s.days_of_week);
    const uniqueSelectedDays = Array.from(new Set(allSelectedDays));
    setSelectedDays(uniqueSelectedDays);
  }, [schedules]);

  // ── Handlers ──
  const toggleDay = (dayNum: number) => {
    setSelectedDays((prev) =>
      prev.includes(dayNum) ? prev.filter((d) => d !== dayNum) : [...prev, dayNum]
    );
  };

  const setDayTime = (dayNum: number, field: 'startTime' | 'endTime', value: string) => {
    setDays((prev) =>
      prev.map((day) =>
        day.dayNum === dayNum ? { ...day, [field]: value } : day
      )
    );
  };

  // Open the copy-to panel for `sourceDayNum`. Pre-selecting nothing forces
  // the user to make a deliberate choice (rather than accidentally
  // overwriting all other days with one tap).
  const openCopyMenu = (sourceDayNum: number) => {
    setCopyMenuFor(sourceDayNum);
    setCopyTargets([]);
  };

  const closeCopyMenu = () => {
    setCopyMenuFor(null);
    setCopyTargets([]);
  };

  const toggleCopyTarget = (dayNum: number) => {
    setCopyTargets((prev) =>
      prev.includes(dayNum) ? prev.filter((d) => d !== dayNum) : [...prev, dayNum],
    );
  };

  const selectAllOtherDays = (sourceDayNum: number) => {
    const others = [1, 2, 3, 4, 5, 6, 7].filter((d) => d !== sourceDayNum);
    // Toggle: if everything is already picked, clear. Otherwise pick all.
    setCopyTargets((prev) => (prev.length === others.length ? [] : others));
  };

  /**
   * Select the OTHER workweek days (excluding the source day). Lets a user
   * set Monday 09–17 then one tap fills the rest of the working week. The
   * working week comes from the building so an Israeli building gets
   * Sun-Thu, a Western one gets Mon-Fri — without any locale guessing.
   */
  const selectAllOtherWorkdays = (sourceDayNum: number) => {
    const others = workweekDays.filter((d) => d !== sourceDayNum);
    setCopyTargets(others);
  };

  const applyCopy = () => {
    if (copyMenuFor === null || copyTargets.length === 0) return;
    const source = days.find((d) => d.dayNum === copyMenuFor);
    if (!source) return;
    setDays((prev) =>
      prev.map((day) =>
        copyTargets.includes(day.dayNum)
          ? { ...day, startTime: source.startTime, endTime: source.endTime }
          : day,
      ),
    );
    // Also enable the target days so the user doesn't have to flip each
    // toggle manually — copying hours implies "schedule these days too".
    setSelectedDays((prev) => Array.from(new Set([...prev, ...copyTargets])));
    closeCopyMenu();
  };

  // ── Validation ──
  const isClearAction = !!existingId && selectedDays.length === 0;

  const isValid = isClearAction || (
    selectedDays.length > 0 &&
    selectedDays.every((dayNum) => {
      const day = days.find((d) => d.dayNum === dayNum);
      return day && day.endTime > day.startTime;
    })
  );

  // ── Build final payload: group selected days by matching times ──
  const buildFinalPayload = () => {
    const scheduleGroups: Array<{ days_of_week: number[]; start_time: string; end_time: string }> = [];

    if (selectedDays.length === 0) return scheduleGroups;

    // Group days by matching times
    const timeGroups: Map<string, number[]> = new Map();
    selectedDays.forEach((dayNum) => {
      const day = days.find((d) => d.dayNum === dayNum);
      if (!day) return;

      const key = `${day.startTime}-${day.endTime}`;
      const existing = timeGroups.get(key) || [];
      timeGroups.set(key, [...existing, dayNum]);
    });

    // Convert time groups to payload format
    Array.from(timeGroups.entries()).forEach(([timeKey, dayNums]) => {
      const [startTime, endTime] = timeKey.split('-');
      scheduleGroups.push({
        days_of_week: dayNums.sort((a, b) => a - b),
        start_time: startTime,
        end_time: endTime,
      });
    });

    return scheduleGroups;
  };

  const mutation = useMutation({
    mutationFn: async () => {
      if (isClearAction && existingId) {
        // Delete all existing schedules for this spot
        if (schedules && schedules.length > 0) {
          for (const schedule of schedules) {
            await apiClient.delete(`/spots/${id}/schedules/${schedule.id}`);
          }
        }
        return;
      }

      // Delete existing schedules before creating new ones
      if (existingId && schedules && schedules.length > 0) {
        for (const schedule of schedules) {
          await apiClient.delete(`/spots/${id}/schedules/${schedule.id}`);
        }
      }

      // Build final payload: group by matching times
      const scheduleGroups = buildFinalPayload();
      for (const group of scheduleGroups) {
        const body = {
          days_of_week: group.days_of_week,
          start_time: group.start_time,
          end_time: group.end_time,
          is_active: isActive,
        };
        await apiClient.post(`/spots/${id}/schedules`, body);
      }
    },
    onSuccess: () => {
      if (!isClearAction) {
        analytics.trackScheduleCreated(selectedDays.length);
      }
      void qc.invalidateQueries({ queryKey: ['spot', id] });
      void qc.invalidateQueries({ queryKey: ['schedules', id] });
      if (spotData?.status === 'occupied' && spotData.active_session) {
        setShowMessageDialog(true);
      } else {
        navigate(`/spots/${id}`);
      }
    },
    onError: () => setApiError('Something went wrong. Please try again.'),
  });

  const handleSendMessage = async (message: string) => {
    setSendingMessage(true);
    try {
      await apiClient.post(`/spots/${id}/notify-occupant`, { message });
    } catch {
      // ignore — occupant notification is best-effort
    } finally {
      setSendingMessage(false);
      navigate(`/spots/${id}`);
    }
  };

  // ── derived labels ───────────────────────────────────────────────────────

  const spotLabel  = spotData ? `${t(spotData.level_code) || spotData.level_label} · ${spotData.spot_number}` : '…';
  const pageTitle  = existingId ? (isClearAction ? t('clear_schedule') : t('edit_schedule')) : t('set_schedule');
  const submitLabel = mutation.isPending
    ? t('saving')
    : isClearAction ? t('clear_schedule')
    : existingId    ? t('update_schedule')
    : t('save_schedule');

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div style={page}>
      <style>{`
        input[type="time"]::-webkit-outer-spin-button,
        input[type="time"]::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
          background: transparent;
          color: #005bc0;
        }
        input[type="time"]::-webkit-calendar-picker-indicator {
          background: transparent;
          cursor: pointer;
          color: #005bc0;
        }
      `}</style>

      {/* ── Top bar ── */}
      <div style={topBar}>
        <button onClick={() => navigate(`/spots/${id}`)} style={backBtn} aria-label="Back">
          <DirectionalIcon icon="arrow_back" style={{ fontSize: '24px' }} />
        </button>
        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 114 26" style={{ height: '22px', width: 'auto', display: 'block' }} aria-label="Lot'N'Go">
            <text x="57" y="18" fontFamily="Manrope, system-ui, sans-serif" fontWeight="700" fontSize="18" fill="#005bc0" textAnchor="middle" letterSpacing="-0.4">{"Lot'N'Go"}</text>
          </svg>
        </div>
        <div style={{ width: '40px', flexShrink: 0 }} />
      </div>

      {/* ── Scrollable body ── */}
      <div style={body}>

        {/* Page header */}
        <section style={{ marginBottom: '28px' }}>
          <h2 style={{ fontFamily: "'Manrope', system-ui, sans-serif", fontSize: '28px', fontWeight: 800, color: '#2b3437', letterSpacing: '-0.02em', margin: '0 0 4px' }}>
            {pageTitle}
          </h2>
          <p style={{ fontSize: '14px', color: '#586064', fontWeight: 500, margin: 0 }}>
            {spotLabel}
          </p>
        </section>

        {/* ── Days and Times List ── */}
        {!isClearAction && (
          <div style={card}>
            <p style={cardLabel}>{t('days_and_hours')}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {getDays(t, language).map(({ full, dayNum }) => {
                const isSelected = selectedDays.includes(dayNum);
                const day = days.find((d) => d.dayNum === dayNum);
                if (!day) return null;

                const dayStartTime = day.startTime;
                const dayEndTime = day.endTime;
                const isTimeValid = dayEndTime > dayStartTime;
                const isRTL = language === 'he';

                return (
                  <div
                    key={dayNum}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      padding: '12px',
                      borderRadius: '12px',
                      background: isSelected ? '#f0f5ff' : '#f8f9fa',
                      border: isSelected ? '1px solid #dde8ff' : '1px solid transparent',
                      direction: isRTL ? 'rtl' : 'ltr',
                    }}
                  >
                    {/* Day row: Toggle + Label + Time inputs */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                      }}
                    >
                      {/* Day toggle */}
                      <button
                        onClick={() => toggleDay(dayNum)}
                        aria-label={`Toggle ${full}`}
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          border: 'none',
                          background: isSelected ? '#005bc0' : '#d1d5db',
                          color: '#ffffff',
                          fontSize: '16px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'all 0.15s',
                        }}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                          {isSelected ? 'check' : ''}
                        </span>
                      </button>

                      {/* Day label */}
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#2b3437' }}>
                          {full}
                        </div>
                      </div>

                      {/* Time inputs */}
                      <div
                        style={{
                          display: 'flex',
                          gap: '8px',
                          alignItems: 'center',
                          flexShrink: 0,
                          opacity: isSelected ? 1 : 0.5,
                          pointerEvents: isSelected ? 'auto' : 'none',
                        }}
                      >
                        <input
                          type="time"
                          value={dayStartTime}
                          onChange={(e) => setDayTime(dayNum, 'startTime', e.target.value)}
                          disabled={!isSelected}
                          style={{
                            ...timeInput,
                            minWidth: '75px',
                            width: '75px',
                            fontSize: '13px',
                            textAlign: 'center',
                            flexShrink: 0,
                            opacity: isSelected ? 1 : 0.5,
                          }}
                        />
                        <span style={{ fontSize: '12px', color: '#737c7f', flexShrink: 0, userSelect: 'none' }}>–</span>
                        <input
                          type="time"
                          value={dayEndTime}
                          onChange={(e) => setDayTime(dayNum, 'endTime', e.target.value)}
                          disabled={!isSelected}
                          style={{
                            ...timeInput,
                            minWidth: '75px',
                            width: '75px',
                            fontSize: '13px',
                            textAlign: 'center',
                            color: !isTimeValid && isSelected ? '#dc2626' : '#2b3437',
                            flexShrink: 0,
                            opacity: isSelected ? 1 : 0.5,
                          }}
                        />
                      </div>
                    </div>

                    {/* Copy-hours-to row. Only meaningful when this day is on
                        and its time range is valid — otherwise there's nothing
                        useful to copy. */}
                    {isSelected && isTimeValid && copyMenuFor !== dayNum && (
                      <button
                        onClick={() => openCopyMenu(dayNum)}
                        style={{
                          display:    'flex',
                          alignItems: 'center',
                          gap:        '6px',
                          alignSelf:  isRTL ? 'flex-end' : 'flex-start',
                          background: 'transparent',
                          border:     'none',
                          padding:    '4px 0',
                          color:      '#005bc0',
                          fontSize:   '12px',
                          fontWeight: 600,
                          cursor:     'pointer',
                        }}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: '16px' }} aria-hidden>
                          content_copy
                        </span>
                        {t('copy_hours_to')}
                      </button>
                    )}

                    {copyMenuFor === dayNum && (
                      <div style={{
                        marginTop:    '4px',
                        padding:      '12px',
                        borderRadius: '10px',
                        background:   '#ffffff',
                        border:       '1px solid #dde8ff',
                      }}>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#005bc0', marginBottom: '10px' }}>
                          {t('copy_hours_to')} {dayStartTime}–{dayEndTime}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                          {getDays(t, language)
                            .filter((d) => d.dayNum !== dayNum)
                            .map((d) => {
                              const picked = copyTargets.includes(d.dayNum);
                              return (
                                <button
                                  key={d.dayNum}
                                  onClick={() => toggleCopyTarget(d.dayNum)}
                                  style={{
                                    padding:      '6px 12px',
                                    borderRadius: '9999px',
                                    border:       picked ? 'none' : '1px solid #dde8ff',
                                    background:   picked ? '#005bc0' : '#f0f5ff',
                                    color:        picked ? '#ffffff' : '#005bc0',
                                    fontSize:     '12px',
                                    fontWeight:   600,
                                    cursor:       'pointer',
                                  }}
                                >
                                  {d.full}
                                </button>
                              );
                            })}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => selectAllOtherWorkdays(dayNum)}
                            style={{
                              flex:         1,
                              minWidth:     '90px',
                              height:       '34px',
                              borderRadius: '8px',
                              border:       '1px solid #dde8ff',
                              background:   '#ffffff',
                              color:        '#005bc0',
                              fontSize:     '12px',
                              fontWeight:   700,
                              cursor:       'pointer',
                            }}
                          >
                            {t('all_workdays')}
                          </button>
                          <button
                            onClick={() => selectAllOtherDays(dayNum)}
                            style={{
                              flex:         1,
                              minWidth:     '90px',
                              height:       '34px',
                              borderRadius: '8px',
                              border:       '1px solid #dde8ff',
                              background:   '#ffffff',
                              color:        '#005bc0',
                              fontSize:     '12px',
                              fontWeight:   700,
                              cursor:       'pointer',
                            }}
                          >
                            {t('all_other_days')}
                          </button>
                          <button
                            onClick={closeCopyMenu}
                            style={{
                              flex:         1,
                              height:       '34px',
                              borderRadius: '8px',
                              border:       '1px solid rgba(43,52,55,0.12)',
                              background:   '#ffffff',
                              color:        '#586064',
                              fontSize:     '12px',
                              fontWeight:   700,
                              cursor:       'pointer',
                            }}
                          >
                            {t('cancel')}
                          </button>
                          <button
                            onClick={applyCopy}
                            disabled={copyTargets.length === 0}
                            style={{
                              flex:         1,
                              height:       '34px',
                              borderRadius: '8px',
                              border:       'none',
                              background:   copyTargets.length === 0 ? '#dbe4e7' : '#005bc0',
                              color:        copyTargets.length === 0 ? '#9ca3af' : '#ffffff',
                              fontSize:     '12px',
                              fontWeight:   700,
                              cursor:       copyTargets.length === 0 ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {t('apply')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {selectedDays.length === 0 && (
              <p style={{ fontSize: '12px', color: '#dc2626', marginTop: '12px', marginBottom: 0 }}>
                {t('select_at_least_one_day')}
              </p>
            )}

            {selectedDays.length > 0 && selectedDays.some((dayNum) => {
              const day = days.find((d) => d.dayNum === dayNum);
              return !day || day.endTime <= day.startTime;
            }) && (
              <p style={{ fontSize: '12px', color: '#dc2626', marginTop: '12px', marginBottom: 0 }}>
                {t('must_be_after_start')}
              </p>
            )}
          </div>
        )}

        {/* ── Schedule status ── */}
        {!isClearAction && (
          <div style={{ ...card, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div style={{
                width:           '48px',
                height:          '48px',
                borderRadius:    '50%',
                background:      '#d8e2ff',
                display:         'flex',
                alignItems:      'center',
                justifyContent:  'center',
                flexShrink:      0,
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: '22px', color: '#005bc0' }}>sync</span>
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '14px', color: '#2b3437', marginBottom: '2px' }}>{t('schedule_status')}</div>
                <div style={{ fontSize: '12px', color: '#586064' }}>
                  {isActive ? t('spot_shared_scheduled') : t('schedule_not_active')}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', background: '#dbe4e7', borderRadius: '12px', padding: '4px', flexShrink: 0 }}>
              <button
                onClick={() => setIsActive(true)}
                style={{
                  padding:      '6px 16px',
                  borderRadius: '8px',
                  border:       'none',
                  fontSize:     '13px',
                  fontWeight:   700,
                  cursor:       'pointer',
                  background:   isActive ? '#ffffff' : 'transparent',
                  color:        isActive ? '#005bc0' : '#737c7f',
                  boxShadow:    isActive ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                  transition:   'all 0.15s',
                }}
              >
                {t('active')}
              </button>
              <button
                onClick={() => setIsActive(false)}
                style={{
                  padding:      '6px 16px',
                  borderRadius: '8px',
                  border:       'none',
                  fontSize:     '13px',
                  fontWeight:   700,
                  cursor:       'pointer',
                  background:   !isActive ? '#ffffff' : 'transparent',
                  color:        !isActive ? '#005bc0' : '#737c7f',
                  boxShadow:    !isActive ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                  transition:   'all 0.15s',
                }}
              >
                {t('paused')}
              </button>
            </div>
          </div>
        )}

        {/* ── Preview chip ── */}
        {isValid && !isClearAction && (
          <div style={{ padding: '14px 16px', borderRadius: '14px', background: '#dde8ff', marginBottom: '12px' }}>
            {(() => {
              // Group selected days by matching times
              const timeGroups: Map<string, number[]> = new Map();
              selectedDays.forEach((dayNum) => {
                const day = days.find((d) => d.dayNum === dayNum);
                if (!day) return;

                const key = `${day.startTime}-${day.endTime}`;
                const existing = timeGroups.get(key) || [];
                timeGroups.set(key, [...existing, dayNum]);
              });

              return (
                <>
                  {Array.from(timeGroups.entries()).map(([timeKey, dayNums]) => {
                    const [startTime, endTime] = timeKey.split('-');
                    const daysLabel = formatDays(dayNums.sort((a, b) => a - b), t, language);
                    return (
                      <div key={timeKey} style={{ fontSize: '13px', fontWeight: 700, color: '#005bc0', marginBottom: '2px' }}>
                        {daysLabel}: {startTime}–{endTime}
                      </div>
                    );
                  })}
                  <div style={{ fontSize: '12px', color: '#005bc0', marginTop: '6px' }}>
                    {t('spot_releases_auto')}{!isActive ? ' · (' + t('paused') + ')' : ''}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {apiError && (
          <div style={{ marginBottom: '12px', padding: '12px 16px', borderRadius: '10px', background: '#fef2f2', color: '#dc2626', fontSize: '13px' }}>
            {apiError}
          </div>
        )}

        {/* ── Action buttons ── */}
        <button
          onClick={() => { setApiError(null); mutation.mutate(); }}
          disabled={!isValid || mutation.isPending}
          style={{
            display:      'block',
            width:        '100%',
            height:       '56px',
            borderRadius: '14px',
            border:       'none',
            fontSize:     '16px',
            fontWeight:   700,
            cursor:       (!isValid || mutation.isPending) ? 'not-allowed' : 'pointer',
            opacity:      (!isValid || mutation.isPending) ? 0.5 : 1,
            marginBottom: '12px',
            background:   isClearAction
              ? 'linear-gradient(135deg, #dc2626, #b91c1c)'
              : 'linear-gradient(135deg, #005bc0, #004fa9)',
            color:        '#f7f7ff',
            boxShadow:    (!isValid || mutation.isPending) ? 'none'
              : isClearAction
                ? '0 8px 24px -4px rgba(220,38,38,0.35)'
                : '0 8px 24px -4px rgba(0,91,192,0.3)',
          }}
        >
          {submitLabel}
        </button>

        <button
          onClick={() => {
            setSelectedDays([]);
            navigate(`/spots/${id}`);
          }}
          style={{
            display:      'block',
            width:        '100%',
            height:       '56px',
            borderRadius: '14px',
            border:       'none',
            background:   'transparent',
            fontSize:     '15px',
            fontWeight:   700,
            color:        '#005bc0',
            cursor:       'pointer',
          }}
        >
          {t('cancel_changes')}
        </button>
      </div>

      {/* ── Message dialog ── */}
      {showMessageDialog && (
        <div style={dialogOverlay}>
          <div style={dialogBox}>
            <div style={{ fontWeight: 700, fontSize: '16px', color: '#2b3437', marginBottom: '6px', fontFamily: "'Manrope', system-ui, sans-serif" }}>
              {t('notify_occupant')}
            </div>
            <div style={{ fontSize: '13px', color: '#586064', marginBottom: '20px', lineHeight: 1.5 }}>
              {t('occupant_using_spot', { apartment: spotData?.active_session?.occupant.apartment })}
            </div>

            {SCHEDULE_MESSAGES.map((msg) => (
              <button
                key={msg}
                onClick={() => handleSendMessage(msg)}
                disabled={sendingMessage}
                style={msgBtn}
              >
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#2b3437', display: 'block', marginBottom: '2px' }}>
                  {msg.includes('need my spot soon') ? t('need_spot_soon') : t('stay_until_end_of_day')}
                </span>
                <span style={{ fontSize: '12px', color: '#586064', fontStyle: 'italic' }}>
                  "{msg}"
                </span>
              </button>
            ))}

            <button
              onClick={() => navigate(`/spots/${id}`)}
              disabled={sendingMessage}
              style={{ ...msgBtn, background: '#f1f4f6', marginTop: '4px' }}
            >
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#586064' }}>{t('skip_notify')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── styles ───────────────────────────────────────────────────────────────────

const page: React.CSSProperties = {
  display:         'flex',
  flexDirection:   'column',
  minHeight:       '100dvh',
  maxWidth:        '430px',
  margin:          '0 auto',
  backgroundColor: '#f8f9fa',
  fontFamily:      "'Inter', system-ui, -apple-system, sans-serif",
};

const topBar: React.CSSProperties = {
  display:              'flex',
  alignItems:           'center',
  justifyContent:       'space-between',
  padding:              '0 8px',
  height:               '64px',
  position:             'sticky',
  top:                  0,
  zIndex:               10,
  backgroundColor:      'rgba(248,249,250,0.85)',
  backdropFilter:       'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  boxShadow:            '0 12px 32px -4px rgba(43,52,55,0.06)',
};

const backBtn: React.CSSProperties = {
  width:           '40px',
  height:          '40px',
  display:         'flex',
  alignItems:      'center',
  justifyContent:  'center',
  background:      'none',
  border:          'none',
  borderRadius:    '9999px',
  cursor:          'pointer',
  color:           '#005bc0',
  flexShrink:      0,
};

const body: React.CSSProperties = {
  flex:       1,
  padding:    '24px 20px 40px',
  overflowY:  'auto',
};

const card: React.CSSProperties = {
  background:   '#ffffff',
  borderRadius: '16px',
  padding:      '20px',
  marginBottom: '12px',
  boxShadow:    '0 12px 32px -4px rgba(43,52,55,0.04)',
};

const cardLabel: React.CSSProperties = {
  fontSize:      '11px',
  fontWeight:    700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color:         '#737c7f',
  marginBottom:  '14px',
  marginTop:     0,
};

const timeInput: React.CSSProperties = {
  background:  'transparent',
  border:      'none',
  fontSize:    '24px',
  fontWeight:  700,
  color:       '#2b3437',
  padding:     0,
  outline:     'none',
  fontFamily:  "'Inter', system-ui, sans-serif",
  width:       '100%',
};

const timeFooter: React.CSSProperties = {
  marginTop:  '14px',
  paddingTop: '12px',
  borderTop:  '1px solid rgba(43,52,55,0.06)',
  fontSize:   '11px',
  color:      '#9ca3af',
  fontStyle:  'italic',
};

const dialogOverlay: React.CSSProperties = {
  position:       'fixed',
  inset:          0,
  background:     'rgba(0,0,0,0.45)',
  display:        'flex',
  alignItems:     'flex-end',
  justifyContent: 'center',
  zIndex:         100,
};

const dialogBox: React.CSSProperties = {
  background:   '#ffffff',
  borderRadius: '20px 20px 0 0',
  padding:      '24px 20px max(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), 36px)',
  width:        '100%',
  maxWidth:     '430px',
};

const msgBtn: React.CSSProperties = {
  display:      'block',
  width:        '100%',
  textAlign:    'start',
  padding:      '14px 16px',
  borderRadius: '12px',
  border:       '1px solid rgba(43,52,55,0.1)',
  background:   '#ffffff',
  cursor:       'pointer',
  marginBottom: '8px',
};
